import {
  EmploymentType,
  JobListingType,
  PayUnit,
} from 'src/graphql/types/jobListings'
import { Endorsement, LicenseClass } from 'src/graphql/types/licenses'

import { PublicTemplateQuestion } from './shortFormQuestions.utils'

export interface ShortFormJobListing {
  jobListingId: string
  title: string
  type?: JobListingType | null
  subtitle?: string | null
  description?: string | null
  benefits?: string[] | null
  payMin?: number | null
  payMax?: number | null
  payUnit?: PayUnit | null
  payCurrency?: string | null
  employmentType?: EmploymentType | null
  licenseClass?: LicenseClass[] | null
  endorsements?: Endorsement[] | null
  minMonthsExperience?: number | null
  minEmploymentHistoryYears?: number | null
  address?: { city?: string | null; state?: string | null } | null
  company?: { companyId?: string | null; companyName?: string | null } | null
  publicQuestions?: PublicTemplateQuestion[] | null
}

const PayUnitSuffixes: Record<PayUnit, string> = {
  [PayUnit.PER_MILE]: '/ mile',
  [PayUnit.HOURLY]: '/ hour',
  [PayUnit.DAILY]: '/ day',
  [PayUnit.WEEKLY]: '/ week',
  [PayUnit.MONTHLY]: '/ month',
  [PayUnit.ANNUAL]: '/ year',
  [PayUnit.PER_TASK]: '/ task',
  [PayUnit.PER_SHIFT]: '/ shift',
}

const formatCurrency = (amount: number, currency: string, compact: boolean) => {
  // Compact only drops cents from whole-dollar amounts ($1,250 / week) —
  // fractional rates like $0.55 / mile must keep their cents
  const dropCents = compact && Number.isInteger(amount)
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    minimumFractionDigits: dropCents ? 0 : 2,
    maximumFractionDigits: dropCents ? 0 : 2,
  }
  try {
    return new Intl.NumberFormat('en-US', { ...options, currency }).format(
      amount
    )
  } catch {
    // payCurrency is an unvalidated VarChar(3) — an invalid code must not
    // crash the public form render, fall back to USD
    return new Intl.NumberFormat('en-US', {
      ...options,
      currency: 'USD',
    }).format(amount)
  }
}

/**
 * Formats the pay range, e.g. "From $1,250.00 / week" or "$0.55 – $0.65 / mile".
 * Pass compact to drop cents from whole-dollar amounts (used in the mobile
 * sticky Apply bar). Returns null when the listing has no pay information.
 */
export const formatPay = (
  jobListing: Pick<
    ShortFormJobListing,
    'payMin' | 'payMax' | 'payUnit' | 'payCurrency'
  >,
  { compact = false }: { compact?: boolean } = {}
): string | null => {
  const { payMin, payMax, payUnit } = jobListing
  const currency = jobListing.payCurrency || 'USD'
  const suffix = payUnit ? ` ${PayUnitSuffixes[payUnit]}` : ''

  if (payMin != null && payMax != null && payMin !== payMax) {
    return `${formatCurrency(payMin, currency, compact)} – ${formatCurrency(
      payMax,
      currency,
      compact
    )}${suffix}`
  }
  if (payMin != null) {
    return `From ${formatCurrency(payMin, currency, compact)}${suffix}`
  }
  if (payMax != null) {
    return `Up to ${formatCurrency(payMax, currency, compact)}${suffix}`
  }
  return null
}

/**
 * Formats license classes, e.g. "Class A CDL" or "Class A or B CDL".
 * NO_CDL alone renders as "No CDL required".
 */
export const formatLicenseClasses = (
  licenseClass?: LicenseClass[] | null
): string | null => {
  if (!licenseClass || licenseClass.length === 0) {
    return null
  }
  const cdlClasses = licenseClass.filter((c) => c !== LicenseClass.NO_CDL)
  if (cdlClasses.length === 0) {
    return 'No CDL required'
  }
  return `Class ${cdlClasses.join(' or ')} CDL`
}

/**
 * Formats minimum experience from months, e.g. "1 year", "18 months".
 */
export const formatMinExperience = (
  minMonthsExperience?: number | null
): string | null => {
  if (minMonthsExperience == null || minMonthsExperience <= 0) {
    return null
  }
  if (minMonthsExperience % 12 === 0) {
    const years = minMonthsExperience / 12
    return `${years} year${years === 1 ? '' : 's'}`
  }
  return `${minMonthsExperience} month${minMonthsExperience === 1 ? '' : 's'}`
}

/**
 * Formats the job location as "City, ST" (either part may be missing).
 */
export const formatLocation = (
  address?: { city?: string | null; state?: string | null } | null
): string | null => {
  const parts = [address?.city, address?.state].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
}

export const hasCompensationInfo = (jobListing: ShortFormJobListing) =>
  Boolean(
    jobListing.employmentType ||
      formatPay(jobListing) ||
      (jobListing.benefits && jobListing.benefits.length > 0)
  )

export const hasRequirementsInfo = (jobListing: ShortFormJobListing) =>
  Boolean(
    formatLicenseClasses(jobListing.licenseClass) ||
      (jobListing.endorsements && jobListing.endorsements.length > 0) ||
      formatMinExperience(jobListing.minMonthsExperience) ||
      jobListing.minEmploymentHistoryYears
  )

/**
 * True when the listing carries job-description content worth rendering
 * alongside the form (subtitle, description, compensation or requirements).
 * When false the page falls back to a single centered card — title and
 * location/company chips above the form (designs 1d desktop / 3c mobile) —
 * so location alone does not count as job info.
 */
export const hasJobInfo = (
  jobListing?: ShortFormJobListing | null
): boolean => {
  if (!jobListing) {
    return false
  }
  return Boolean(
    jobListing.subtitle ||
      jobListing.description ||
      hasCompensationInfo(jobListing) ||
      hasRequirementsInfo(jobListing)
  )
}
